import { useEffect, useState } from "react";
import { Briefcase, Plus, X, Loader2, Check, Users, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, Loading, Empty, CreatorName } from "./common";
import { Button } from "./ui/button";

const field = "w-full surface-2 border border-white/10 rounded-xl px-3 h-10 text-sm outline-none focus:border-gold/50";
const STATUS = { pending: "text-amber-400", accepted: "text-emerald-400", rejected: "text-rose-400" };

export function CityJobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [mine, setMine] = useState([]);
  const [apps, setApps] = useState([]);
  const [biz, setBiz] = useState([]);
  const [posting, setPosting] = useState(false);
  const [applyTo, setApplyTo] = useState(null);
  const [review, setReview] = useState(null);

  const load = () => {
    api.get("/virtucity/jobs").then(({ data }) => setJobs(data)).catch(() => setJobs([]));
    api.get("/virtucity/jobs/posted").then(({ data }) => setMine([...data].sort((a, b) => (a.status === "open" ? 0 : 1) - (b.status === "open" ? 0 : 1)))).catch(() => {});
    api.get("/virtucity/my-applications").then(({ data }) => setApps(data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/virtucity/jobs/my-businesses").then(({ data }) => setBiz(data)).catch(() => {}); }, []);

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="vc-jobs">
      <div className="lg:col-span-2 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold">Open city jobs</div>
          {biz.length > 0 && <Button onClick={() => setPosting(true)} className="btn-gold text-black rounded-full h-9" data-testid="vc-post-job-btn"><Plus size={15} className="mr-1" />Post a job</Button>}
        </div>
        {!jobs ? <Loading /> : jobs.length === 0 ? <Empty icon={Briefcase} title="No open jobs right now" hint="Business owners can post roles here, and citizens can apply before joining the city." /> :
          jobs.map((j) => <JobCard key={j.id} j={j} user={user} onApply={() => setApplyTo(j)} />)}
      </div>
      <div className="space-y-4">
        {mine.length > 0 && <div className="surface rounded-2xl p-5" data-testid="vc-my-posted-jobs">
          <div className="font-semibold mb-3">Your job posts</div>
          {mine.map((j) => <div key={j.id} className={`flex items-center justify-between py-2 border-b border-white/5 last:border-0 ${j.status === "open" ? "" : "opacity-50"}`}>
            <div className="min-w-0"><div className="text-sm font-medium truncate">{j.title}</div><div className="text-[11px] text-muted-foreground">{j.business?.name} · <span className={j.status === "open" ? "text-emerald-400" : ""}>{j.status === "open" ? "Open" : "Closed"}</span></div></div>
            <button onClick={() => setReview(j)} className="text-xs text-gold flex items-center gap-1 shrink-0" data-testid={`vc-review-${j.id}`}><Users size={13} />{j.applicant_count}</button>
          </div>)}
        </div>}
        <div className="surface rounded-2xl p-5" data-testid="vc-my-applications">
          <div className="font-semibold mb-3">Your applications</div>
          {apps.length === 0 ? <p className="text-xs text-muted-foreground">You haven't applied to any city jobs yet.</p> :
            apps.map((a) => <div key={a.id} className="flex justify-between py-1.5 text-sm"><span className="truncate">{a.job?.title} · <span className="text-muted-foreground">{a.job?.business?.name}</span></span><span className={`text-[11px] font-semibold uppercase ${STATUS[a.status]}`}>{a.status}</span></div>)}
        </div>
      </div>
      {posting && <PostJob biz={biz} onClose={() => setPosting(false)} onDone={() => { setPosting(false); load(); }} />}
      {applyTo && <ApplyModal job={applyTo} onClose={() => setApplyTo(null)} onDone={() => { setApplyTo(null); load(); }} />}
      {review && <ReviewModal job={review} onClose={() => { setReview(null); load(); }} />}
    </div>
  );
}

function JobCard({ j, user, onApply }) {
  return (
    <div className="surface rounded-2xl p-5" data-testid={`vc-job-card-${j.id}`}>
      <div className="flex justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-lg">{j.title}</div>
          <div className="text-xs text-gold">{j.business?.name} · {j.job_type}{j.pay ? ` · ${j.pay}` : ""}</div>
        </div>
        {j.is_mine ? <span className="text-[11px] text-muted-foreground shrink-0">Your post</span>
          : j.my_application ? <span className={`text-[11px] font-semibold uppercase shrink-0 ${STATUS[j.my_application]}`} data-testid={`vc-job-status-${j.id}`}>{j.my_application}</span>
          : <Button onClick={onApply} disabled={!user} className="btn-blue rounded-full h-9 shrink-0" data-testid={`vc-apply-${j.id}`}>Apply</Button>}
      </div>
      {j.description && <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line">{j.description}</p>}
      {j.requirements && <p className="text-xs mt-2"><span className="text-muted-foreground">Requirements:</span> {j.requirements}</p>}
      <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground">
        {j.poster && <><Avatar src={j.poster.avatar_url} name={j.poster.display_name} size={20} /><CreatorName user={j.poster} /> ·</>}{j.applicant_count} {j.applicant_count === 1 ? "applicant" : "applicants"}
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="surface rounded-2xl p-5 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex justify-between items-center mb-4"><div className="font-display font-semibold text-lg">{title}</div><button onClick={onClose} data-testid={`${testid}-close`}><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
}

function PostJob({ biz, onClose, onDone }) {
  const [f, setF] = useState({ biz: `${biz[0].kind}:${biz[0].id}`, title: "", description: "", pay: "", job_type: "Part-time", requirements: "", esx_job: "", esx_grade: 0 });
  const [busy, setBusy] = useState(false);
  const [maps, setMaps] = useState([]);
  useEffect(() => { api.get("/virtucity/job-mappings").then(({ data }) => setMaps(data)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const maxGrade = maps.find((m) => m.esx_job === f.esx_job)?.max_grade ?? 0;
  const submit = async () => {
    if (f.title.trim().length < 3) return toast.error("Add a job title");
    const [business_kind, ...rest] = f.biz.split(":");
    setBusy(true);
    try {
      await api.post("/virtucity/jobs", { business_kind, business_id: rest.join(":"), title: f.title, description: f.description, pay: f.pay || null, job_type: f.job_type, requirements: f.requirements || null, esx_job: f.esx_job.trim().toLowerCase() || null, esx_grade: Number(f.esx_grade) || 0 });
      toast.success("Job posted"); onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Post a city job" onClose={onClose} testid="vc-post-job-modal">
      <div className="space-y-3">
        <select value={f.biz} onChange={set("biz")} className={field} data-testid="vc-job-business">
          {biz.map((b) => <option key={`${b.kind}:${b.id}`} value={`${b.kind}:${b.id}`}>{b.name} ({b.kind === "city" ? "City" : "VirtuStars"})</option>)}
        </select>
        <input value={f.title} onChange={set("title")} placeholder="Job title, e.g. Bartender" className={field} data-testid="vc-job-title" />
        <div className="grid grid-cols-2 gap-2">
          <input value={f.pay} onChange={set("pay")} placeholder="Pay, e.g. $1,500/wk" className={field} data-testid="vc-job-pay" />
          <select value={f.job_type} onChange={set("job_type")} className={field} data-testid="vc-job-type">{["Part-time", "Full-time", "Gig", "Contract"].map((t) => <option key={t}>{t}</option>)}</select>
        </div>
        <textarea value={f.description} onChange={set("description")} placeholder="What will they do?" rows={4} className={`${field} h-auto py-2`} data-testid="vc-job-description" />
        <input value={f.requirements} onChange={set("requirements")} placeholder="Requirements (optional)" className={field} data-testid="vc-job-requirements" />
        <div className="surface-2 rounded-xl p-3">
          <div className="text-[11px] text-muted-foreground mb-2">In-city job (ESX): set automatically in VirtuCity when you hire someone</div>
          <div className="grid grid-cols-[1fr_90px] gap-2">
            <select value={f.esx_job} onChange={(e) => setF({ ...f, esx_job: e.target.value, esx_grade: 0 })} className={field} data-testid="vc-job-esx-job">
              <option value="">No in-city job</option>{maps.map((m) => <option key={m.esx_job} value={m.esx_job}>{m.label}</option>)}
            </select>
            <select value={f.esx_grade} onChange={set("esx_grade")} disabled={!f.esx_job} className={field} data-testid="vc-job-esx-grade">
              {Array.from({ length: maxGrade + 1 }, (_, g) => <option key={g} value={g}>Grade {g}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={submit} disabled={busy} className="btn-gold text-black rounded-full w-full" data-testid="vc-job-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : "Post job"}</Button>
      </div>
    </Modal>
  );
}

function ApplyModal({ job, onClose, onDone }) {
  const [msg, setMsg] = useState("");
  const [cname, setCname] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/virtucity/jobs/${job.id}/apply`, { message: msg, character_name: cname || null }); toast.success("Application sent"); onDone(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Apply: ${job.title}`} onClose={onClose} testid="vc-apply-modal">
      <div className="space-y-3">
        <input value={cname} onChange={(e) => setCname(e.target.value)} placeholder="Character name (optional)" className={field} data-testid="vc-apply-character" />
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Why you're a great fit" rows={4} className={`${field} h-auto py-2`} data-testid="vc-apply-message" />
        <Button onClick={submit} disabled={busy} className="btn-blue rounded-full w-full" data-testid="vc-apply-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : <><Send size={14} className="mr-1.5" />Send application</>}</Button>
      </div>
    </Modal>
  );
}

function ReviewModal({ job, onClose }) {
  const [apps, setApps] = useState(null);
  const load = () => api.get(`/virtucity/jobs/${job.id}/applications`).then(({ data }) => setApps(data)).catch(() => setApps([]));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const decide = async (a, d) => {
    try {
      const { data } = await api.post(`/virtucity/applications/${a.id}/${d}`);
      toast.success(d === "accept" ? (data.esx_sync_queued ? "Hired! Their in-city job will be set automatically." : "Hired!") : "Declined");
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const close = async () => { try { await api.post(`/virtucity/jobs/${job.id}/close`); toast.success("Job closed"); onClose(); } catch (e) { toast.error(errText(e)); } };
  return (
    <Modal title={`Applicants: ${job.title}`} onClose={onClose} testid="vc-review-modal">
      {!apps ? <Loading /> : apps.length === 0 ? <p className="text-sm text-muted-foreground">No applicants yet.</p> : apps.map((a) => (
        <div key={a.id} className="surface-2 rounded-xl p-3 mb-2" data-testid={`vc-applicant-${a.id}`}>
          <div className="flex items-center gap-2"><Avatar src={a.applicant?.avatar_url} name={a.applicant?.display_name} size={28} /><div className="min-w-0 flex-1 text-sm font-medium truncate">{a.applicant?.display_name}{a.character_name ? ` · ${a.character_name}` : ""}</div>
            <span className={`text-[11px] font-semibold uppercase ${STATUS[a.status]}`}>{a.status}</span></div>
          {a.message && <p className="text-xs text-muted-foreground mt-2">{a.message}</p>}
          {a.status === "pending" && <div className="flex gap-2 mt-2">
            <Button onClick={() => decide(a, "accept")} className="btn-gold text-black rounded-full h-8 text-xs flex-1" data-testid={`vc-accept-${a.id}`}><Check size={13} className="mr-1" />Hire</Button>
            <Button onClick={() => decide(a, "reject")} variant="outline" className="rounded-full h-8 text-xs flex-1 border-white/15" data-testid={`vc-reject-${a.id}`}>Decline</Button>
          </div>}
        </div>
      ))}
      {job.status === "open" && <Button onClick={close} variant="outline" className="rounded-full w-full mt-3 border-white/15" data-testid="vc-close-job">Close this job</Button>}
    </Modal>
  );
}
